/**
 * Forward and reverse geocoding through the Atlas backend's Google Maps routes. Used to add a
 * fixed-route stop by address, to centre the map on a searched place, and to label reports.
 */
import i18next from 'i18next';

import { atlasFetch } from './api/atlasBackendClient';
import { getAtlasApiBaseUrl } from './getAtlasApiBaseUrl';

export interface GeocodeResult {
  /** Full formatted address, prefixed by the place name when Google returns one. */
  displayName: string;
  /** A shorter label suitable for a stop name (first 1-2 components). */
  shortName: string;
  lat: number;
  lng: number;
}

export interface GeocodeBias {
  latitude: number;
  longitude: number;
}

export interface SearchAddressOptions {
  signal?: AbortSignal;
  limit?: number;
  near?: GeocodeBias;
}

export interface RawPlaceResult {
  latitude?: number | null;
  longitude?: number | null;
  name?: string | null;
  address?: string | null;
}

interface RawAddressComponent {
  long_name?: string;
  types?: string[];
}

export interface RawReverseGeocodeResult {
  formatted_address?: string;
  address_components?: RawAddressComponent[];
}

const DEFAULT_SEARCH_LIMIT = 6;
const ACCEPTED_STATUSES = new Set(['OK', 'ZERO_RESULTS']);
const SUBURB_TYPES = ['neighborhood', 'sublocality', 'locality'];
const CITY_TYPES = ['postal_town', 'locality', 'colloquial_area', 'administrative_area_level_2'];

export function geocodeLanguage(language: string | undefined = i18next.language): string {
  return language?.toLowerCase().startsWith('pt') ? 'pt-BR' : 'en';
}

function firstComponents(name: string): string {
  return name.split(',').slice(0, 2).join(',').trim() || name;
}

/** Pure transform of backend place-search rows → GeocodeResult[] (exported for unit testing). */
export function mapPlaceResults(data: RawPlaceResult[] | undefined): GeocodeResult[] {
  if (!Array.isArray(data)) return [];
  return data
    .map((r) => {
      const name = (r.name ?? '').trim();
      const address = (r.address ?? '').trim();
      const displayName = name && !address.startsWith(name) ? [name, address].filter(Boolean).join(', ') : address || name;
      const lat = typeof r.latitude === 'number' ? r.latitude : NaN;
      const lng = typeof r.longitude === 'number' ? r.longitude : NaN;
      return { displayName, shortName: firstComponents(displayName), lat, lng };
    })
    .filter((r) => r.displayName.length > 0 && Number.isFinite(r.lat) && Number.isFinite(r.lng));
}

function componentOfType(results: RawReverseGeocodeResult[], types: string[], exclude?: string): string | undefined {
  for (const type of types) {
    for (const result of results) {
      const match = result.address_components?.find((c) => c.types?.includes(type) && c.long_name && c.long_name !== exclude);
      if (match) return match.long_name;
    }
  }
  return undefined;
}

/** Pure transform of Google reverse-geocode results → an area label such as "Newtown, Sydney". */
export function mapReverseGeocodeResults(
  data: RawReverseGeocodeResult[] | undefined,
  lat: number,
  lng: number,
): GeocodeResult | null {
  if (!Array.isArray(data) || data.length === 0) return null;
  const displayName = (data.find((r) => r.formatted_address)?.formatted_address ?? '').trim();
  const suburb = componentOfType(data, SUBURB_TYPES);
  const city = componentOfType(data, CITY_TYPES, suburb);
  const area = [suburb, city].filter((part): part is string => !!part);
  const shortName = area.length > 0 ? area.join(', ') : firstComponents(displayName);
  if (!shortName) return null;
  return { displayName: displayName || shortName, shortName, lat, lng };
}

async function postGoogleMaps<T>(path: string, body: unknown, signal?: AbortSignal): Promise<T> {
  const response = await atlasFetch(`${getAtlasApiBaseUrl()}/api/google-maps/${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
    signal,
  });
  if (!response.ok) {
    throw new Error(`Geocoding failed: ${response.status}`);
  }
  return (await response.json()) as T;
}

/**
 * Search for places matching a free-text query, biased towards `near` when given. Returns [] for
 * queries shorter than 3 chars. Pass an AbortSignal to cancel a superseded search (type-ahead).
 */
export async function searchAddress(query: string, options: SearchAddressOptions = {}): Promise<GeocodeResult[]> {
  const q = query.trim();
  if (q.length < 3) return [];
  const { signal, limit = DEFAULT_SEARCH_LIMIT, near } = options;
  const data = await postGoogleMaps<{ status?: string; error_message?: string | null; results?: RawPlaceResult[] }>(
    'place-search',
    {
      query: q,
      region: null,
      language: geocodeLanguage(),
      limit,
      location_bias: near ? { latitude: near.latitude, longitude: near.longitude } : null,
    },
    signal,
  );
  if (data.status && !ACCEPTED_STATUSES.has(data.status)) {
    throw new Error(`Geocoding failed: ${data.status}`);
  }
  return mapPlaceResults(data.results);
}

/**
 * Reverse-geocode a coordinate to a short area label (e.g. "Capitol Hill, Seattle").
 * Best-effort: returns null on any failure so callers can fall back.
 */
export async function reverseGeocode(lat: number, lng: number, signal?: AbortSignal): Promise<GeocodeResult | null> {
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  try {
    const data = await postGoogleMaps<{ status?: string; results?: RawReverseGeocodeResult[] }>(
      'reverse-geocode',
      { latitude: lat, longitude: lng },
      signal,
    );
    if (data.status !== 'OK') return null;
    return mapReverseGeocodeResults(data.results, lat, lng);
  } catch {
    return null;
  }
}
